import { useState, useRef } from "react";
import api from "@/lib/api";
import { UploadCloud, X, Loader2, Plus } from "lucide-react";

/**
 * GalleryUploader - multi-image uploader.
 * Props:
 *   value: string[] of URLs
 *   onChange: (urls: string[]) => void
 *   folder: Cloudinary folder
 */
export default function GalleryUploader({ value = [], onChange, folder = "coastal-bend/projects", testid = "gallery" }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const uploadOne = async (file) => {
    const { data: sig } = await api.get(`/cloudinary/signature?folder=${encodeURIComponent(folder)}`);
    const form = new FormData();
    form.append("file", file);
    form.append("api_key", sig.api_key);
    form.append("timestamp", sig.timestamp);
    form.append("signature", sig.signature);
    form.append("folder", sig.folder);
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`);
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText).secure_url);
        else reject(new Error("Upload failed"));
      };
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.send(form);
    });
  };

  const onFiles = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError("");
    const current = [...value];
    try {
      for (const f of files) {
        if (!f.type.startsWith("image/") || f.size > 10 * 1024 * 1024) continue;
        const url = await uploadOne(f);
        current.push(url);
        onChange([...current]);
      }
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {value.map((url, i) => (
          <div key={url + i} className="relative aspect-[4/3] rounded-lg overflow-hidden group" data-testid={`${testid}-thumb-${i}`}>
            <img src={url} alt={`gallery ${i + 1}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              style={{background: 'rgba(11,19,43,0.85)', color: '#fca5a5', border: '1px solid rgba(255,255,255,0.1)'}}
              data-testid={`${testid}-remove-${i}`}
            >
              <X size={14}/>
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="aspect-[4/3] rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 text-sm transition-colors"
          style={{borderColor: 'rgba(255,255,255,0.12)', color: 'var(--text-muted)', background:'rgba(11,19,43,0.4)'}}
          data-testid={`${testid}-add`}
        >
          {busy ? (<><Loader2 size={18} className="animate-spin" style={{color:'var(--accent)'}}/><span>Uploading… {progress}%</span></>)
                : (<><Plus size={18} style={{color:'var(--accent)'}}/><span>Add photos</span></>)}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => onFiles(Array.from(e.target.files || []))}
        data-testid={`${testid}-input`}
      />
      {error && <div className="text-xs mt-2" style={{color:'#fca5a5'}}>{error}</div>}
    </div>
  );
}
